import { styled } from "@mui/material/styles";
import { HOME, LOGIN, PASSWORD_RESET, PROTECTED } from "constants/routes";
import Protected from "containers/Protected";
import { MaterialDesignContent, SnackbarProvider } from "notistack";
import React, { Fragment, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import Login from "views/Auth/Login";
import NotFound from "views/Auth/NotFound";
import PasswordReset from "views/Auth/PasswordReset";

const STATUS_SCRIPT_SRC = "https://zc0kcyzv912v.statuspage.io/embed/script.js";
const BEAMER_SCRIPT_SRC = "https://app.getbeamer.com/js/beamer-embed.js";
const BEAMER_CONFIG = {
  product_id: "JrxcSycF16181",
  selector: ".beamerTrigger",
};

const StyledMaterialDesignContent = styled(MaterialDesignContent)(
  ({ theme }) => ({
    "&.notistack-MuiContent-success": {
      backgroundColor: theme.palette.success.main,
    },
    "&.notistack-MuiContent-error": {
      backgroundColor: theme.palette.error.main,
    },
    "&.notistack-MuiContent-warning": {
      backgroundColor: theme.palette.warning.main,
    },
    "&.notistack-MuiContent-info": {
      backgroundColor: theme.palette.info.main,
    },
  }),
);

export default function App() {
  useEffect(() => {
    if (!document.querySelector(`script[src="${STATUS_SCRIPT_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = STATUS_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }

    // Beamer: set `window.beamer_config` THEN inject the embed. The embed
    // reads the global on load, so config must exist first. Previously set
    // as an inline <script> in index.html, which Firebase Hosting's CSP
    // (no 'unsafe-inline' in script-src) blocked on stage/prod.
    if (!document.querySelector(`script[src="${BEAMER_SCRIPT_SRC}"]`)) {
      window.beamer_config = BEAMER_CONFIG;
      const script = document.createElement("script");
      script.src = BEAMER_SCRIPT_SRC;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

  // A file dropped anywhere the page doesn't handle it is opened by the
  // browser, which replaces the whole app with the rendered file and loses
  // everything on screen — grid filters, a half-filled dialog, the lot. The
  // upload dialogs take their own drops; this catches the misses, which are
  // most likely exactly when one is open and the file lands on its title bar
  // or the backdrop an inch away.
  //
  // Drop zones handle the event before it reaches the window, so this only
  // ever sees what nothing claimed. Files only: dragging within the page
  // (the sortable lists use pointer events, not this) is untouched.
  useEffect(() => {
    const swallow = (event) => {
      if (Array.from(event.dataTransfer?.types || []).includes("Files")) {
        event.preventDefault();
      }
    };
    window.addEventListener("dragover", swallow);
    window.addEventListener("drop", swallow);
    return () => {
      window.removeEventListener("dragover", swallow);
      window.removeEventListener("drop", swallow);
    };
  }, []);

  return (
    <SnackbarProvider
      maxSnack={3}
      preventDuplicate
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      Components={{
        success: StyledMaterialDesignContent,
        error: StyledMaterialDesignContent,
        warning: StyledMaterialDesignContent,
        info: StyledMaterialDesignContent,
      }}
    >
      <BrowserRouter
        future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true,
        }}
      >
        <Suspense fallback={<Fragment />}>
          <Routes>
            <Route path={HOME} element={<Protected />} />
            <Route path={LOGIN} element={<Login />} />
            <Route path={PASSWORD_RESET} element={<PasswordReset />} />
            <Route path={PROTECTED}>
              <Route index path="*" element={<Protected />} />
            </Route>
            <Route path={"*"} element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </SnackbarProvider>
  );
}
